import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGlobe } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const LanguageSelector = forwardRef(({ isMobile = false, onToggle, adaptiveTextColor = '#101957', adaptiveHoverBg = 'rgba(0,0,0,0.05)' }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { i18n } = useTranslation();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
    { code: 'he', name: 'Hebrew', nativeName: 'עברית' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  ];

  // Expose toggle function for external control via ref
  useImperativeHandle(ref, () => ({
    toggle: () => {
      setIsOpen(prev => !prev);
    },
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  // Expose toggle function for external control
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (onToggle) onToggle(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
    setIsOpen(false);
    
    // Set document direction for RTL languages
    if (langCode === 'he') {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  };

  return (
    <div className={`relative ${isMobile ? 'w-full' : 'inline-block'}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`p-2 rounded-full transition-all duration-300 relative ${isMobile ? 'hidden' : ''}`}
        style={{ backgroundColor: 'transparent' }}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = adaptiveHoverBg}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        data-testid="language-selector-btn"
      >
        <FiGlobe className="text-xl transition-colors duration-300" style={{ color: adaptiveTextColor }} />
      </button>

      {/* Tooltip - NO ARROW (Desktop only) */}
      <AnimatePresence>
        {showTooltip && !isOpen && !isMobile && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="absolute pointer-events-none z-50"
            style={{
              top: 'calc(100% + 8px)',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <div
              className="px-3 py-1.5 text-xs font-medium whitespace-nowrap rounded-lg"
              style={{
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                color: 'white'
              }}
            >
              Region and Language
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Local Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for mobile */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30"
                style={{ zIndex: 10000 }}
                onClick={() => setIsOpen(false)}
              />
            )}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`bg-white rounded-xl shadow-2xl overflow-hidden ${
                isMobile 
                  ? 'fixed inset-x-0 mx-auto' 
                  : 'absolute top-full right-0 mt-2'
              }`}
              style={{
                border: '1px solid rgba(0,0,0,0.1)',
                zIndex: isMobile ? 10001 : 50,
                ...(isMobile ? { 
                  top: '50%',
                  transform: 'translateY(-50%)',
                  maxWidth: '280px',
                  width: '85%'
                } : {
                  minWidth: '220px'
                })
              }}
            >
            {/* Arrow pointer (Desktop only) */}
            {!isMobile && (
              <div 
                className="absolute bottom-full right-3"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderBottom: '8px solid white',
                }}
              />
            )}

            {/* Language List */}
            <div className="py-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between group"
                >
                  <span 
                    className="font-medium group-hover:translate-x-1 transition-transform"
                    style={{ color: '#101957' }}
                  >
                    {lang.name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {lang.nativeName}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
});

LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;
